/**
 * Pure helpers for the Roles & Access editor — no React, no network, so the
 * tricky bits (group mixed-state, clamp-to-max, diffing a draft against the
 * saved role) are unit-testable without mounting anything.
 */
import { ADMIN_SECTIONS, levelFor, type RolePermissions, type AccessLevel } from '@platform/shared-types';

export interface LevelCounts {
  edit: number;
  view: number;
  off: number;
}

/** Edit/View/Off counts across the given sections (defaults to every section). */
export function countLevels(perms: RolePermissions, sectionIds?: string[]): LevelCounts {
  const ids = sectionIds ?? ADMIN_SECTIONS.map((s) => s.id);
  const out: LevelCounts = { edit: 0, view: 0, off: 0 };
  for (const id of ids) {
    const l = levelFor(perms, id);
    if (l === 'edit') out.edit++;
    else if (l === 'view') out.view++;
    else out.off++;
  }
  return out;
}

/** Section ids belonging to a group, in catalogue order. */
export function sectionsInGroup(group: string): string[] {
  return ADMIN_SECTIONS.filter((s) => s.group === group).map((s) => s.id);
}

/**
 * The group header's own level: the shared level when every section in the
 * group agrees, 'mixed' when they don't, null when the group is empty.
 */
export function groupLevel(perms: RolePermissions, group: string): AccessLevel | null | 'mixed' {
  const ids = sectionsInGroup(group);
  if (ids.length === 0) return null;
  const first = levelFor(perms, ids[0]);
  for (const id of ids.slice(1)) {
    if (levelFor(perms, id) !== first) return 'mixed';
  }
  return first;
}

const RANK: Record<AccessLevel, number> = { view: 1, edit: 2 };

/** `value` capped at `max` — Off/null always wins regardless of max. */
export function clampLevel(value: AccessLevel | null, max: AccessLevel | null): AccessLevel | null {
  if (value == null) return null;
  if (max == null) return null;
  return RANK[value] <= RANK[max] ? value : max;
}

/**
 * Setting a group's header to `chosen` sets every child in the group to
 * min(chosen, that child's own max) — a child the editor can't grant that
 * high is clamped down rather than blocking the whole group set.
 */
export function applyGroupLevel(
  perms: RolePermissions,
  group: string,
  chosen: AccessLevel | null,
  maxFor: (sectionId: string) => AccessLevel | null,
): RolePermissions {
  const next = { ...perms };
  for (const id of sectionsInGroup(group)) {
    const clamped = clampLevel(chosen, maxFor(id));
    if (clamped == null) delete next[id];
    else next[id] = clamped;
  }
  return next;
}

/** Set (or clear, when `value` is null) a single section's level. */
export function setLevel(perms: RolePermissions, sectionId: string, value: AccessLevel | null): RolePermissions {
  const next = { ...perms };
  if (value == null) delete next[sectionId];
  else next[sectionId] = value;
  return next;
}

export interface PermissionChange {
  sectionId: string;
  label: string;
  from: AccessLevel | null;
  to: AccessLevel | null;
  sensitive: boolean;
}

/** Every section whose level differs between `saved` and `draft`, catalogue order. */
export function diffPermissions(saved: RolePermissions, draft: RolePermissions): PermissionChange[] {
  const out: PermissionChange[] = [];
  for (const s of ADMIN_SECTIONS) {
    const from = levelFor(saved, s.id);
    const to = levelFor(draft, s.id);
    if (from !== to) out.push({ sectionId: s.id, label: s.label, from, to, sensitive: !!s.sensitive });
  }
  return out;
}

export function hasSensitiveChange(changes: PermissionChange[]): boolean {
  return changes.some((c) => c.sensitive);
}

/** Display text for a single level, matching the mockup's Off/View/Edit labels. */
export function levelLabel(level: AccessLevel | null | undefined): 'Off' | 'View' | 'Edit' {
  return level === 'edit' ? 'Edit' : level === 'view' ? 'View' : 'Off';
}
